import React, { useState } from 'react';
import { Reorder, useDragControls } from 'framer-motion';
import { GripVerticalIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { DELIVERABLE_STATUSES, DELIVERABLE_TYPES, VIDEO_FORMATS } from '../../types/portal';
import type { Deliverable } from '../../types/portal';
import { deliverableStage, isVersioned, metaLabel, stageHeading } from '../../utils/deliverables';
import { VersionHistory } from './VersionHistory';
import { TranscriptUpload } from './TranscriptUpload';
import { TranscriptRevisions } from './TranscriptRevisions';
import { isDriveFolder, toDrivePreviewUrl } from '../../utils/drive';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from './adminStyles';
interface DeliverableAdminProps {
  deliverable: Deliverable;
  /** Reports which row is currently lifted, or null once it is set down. */
  onLift: (id: string | null) => void;
}
export function DeliverableAdmin({
  deliverable,
  onLift
}: DeliverableAdminProps) {
  const dragControls = useDragControls();
  const [carrying, setCarrying] = useState(false);
  const {
    updateDeliverable,
    removeDeliverable,
    publishVersion,
    resolveChangeRequest,
    notify,
    focusDeliverableId
  } = usePortal();

  /** 'details' shows the client feedback; 'edit' also shows the configuration. */
  const [panel, setPanel] = useState<'closed' | 'details' | 'edit'>(focusDeliverableId === deliverable.id ? 'details' : 'closed');
  const open = panel !== 'closed';
  const editingRow = panel === 'edit';
  const setOpen = (next: boolean) => setPanel(next ? 'edit' : 'closed');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [draft, setDraft] = useState<Deliverable>(deliverable);
  function edit(patch: Partial<Deliverable>) {
    setDraft(current => ({
      ...current,
      ...patch
    }));
  }
  function save() {
    updateDeliverable(deliverable.id, {
      name: draft.name.trim() || deliverable.name,
      type: draft.type,
      quantity: draft.quantity,
      status: draft.status,
      version: draft.version,
      driveUrl: draft.driveUrl,
      audioUrl: draft.audioUrl,
      videoFormat: draft.type === 'Video' ? draft.videoFormat ?? '16:9 Landscape' : undefined,
      internalNote: draft.internalNote,
      visibleToClient: draft.visibleToClient,
      transcript: draft.transcript
    });
    notify(`${draft.name || deliverable.name} · changes saved`);
    setOpen(false);
  }
  function publish() {
    updateDeliverable(deliverable.id, {
      driveUrl: draft.driveUrl,
      audioUrl: draft.audioUrl
    });
    publishVersion(deliverable.id, draft.version);
    notify(`${deliverable.name} · ${draft.version || 'new version'} published for review`);
  }
  const outstanding = deliverable.changeRequests.filter(request => !request.resolved);
  /** Completed requests stay on the record, newest first. */
  const completed = deliverable.changeRequests.filter(request => request.resolved).reverse();
  return <Reorder.Item value={deliverable} dragListener={false} dragControls={dragControls} onDragStart={() => {
    setCarrying(true);
    onLift(deliverable.id);
  }} onDragEnd={() => {
    setCarrying(false);
    onLift(null);
  }}
  /* The row itself is carried, so it stays fully visible and readable. */ animate={carrying ? {
    backgroundColor: 'rgba(255,255,255,0.05)',
    boxShadow: '0 18px 40px rgba(0,0,0,0.55)'
  } : {
    backgroundColor: 'rgba(255,255,255,0)',
    boxShadow: '0 0 0 rgba(0,0,0,0)'
  }} transition={{
    duration: 0.18,
    ease: [0.23, 1, 0.32, 1]
  }} style={{
    position: 'relative',
    zIndex: carrying ? 30 : 0
  }} className={['border-b border-ruleDark', carrying ? 'cursor-grabbing rounded-[2px] ring-1 ring-paper/25' : ''].join(' ')} data-id="element-184" data-mp="1rzfnl68wfpsp">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-4 px-3 py-6" data-id="element-185" data-mp="2oq87keyye1zr">
        <span role="button" tabIndex={0} onPointerDown={event => {
        event.preventDefault();
        dragControls.start(event);
      }} aria-label={`Reorder ${deliverable.name}`} title="Drag to reorder" className="shrink-0 touch-none cursor-grab text-paper/30 transition-colors duration-150 ease-out hover:text-paper/70 active:cursor-grabbing" data-id="element-186" data-mp="1mb91k5gfcir1">
          <GripVerticalIcon className="h-5 w-5" strokeWidth={1.25} data-id="element-187" data-mp="12m8buf1q44ym" />
        </span>

        {/* The row itself opens the deliverable's details and client feedback. */}
        <button type="button" onClick={() => setPanel(current => current === 'details' ? 'closed' : 'details')} aria-expanded={open} className="min-w-0 flex-1 text-left" data-id="element-188" data-mp="tnwb3k757qsg">
          <p className="font-display text-2xl leading-none sm:text-3xl" data-id="element-189" data-mp="jr0cq7aykl1m">{deliverable.name}</p>
          <p className="label-sm mt-3 text-paper/40" data-id="element-190" data-mp="on8l91unhfzt">
            {metaLabel(deliverable)} · {stageHeading(deliverableStage(deliverable.status))} ·{' '}
            {deliverable.status}
            {outstanding.length > 0 ? ` · ${outstanding.length} change${outstanding.length === 1 ? '' : 's'} requested` : ''}
          </p>
        </button>

        <button type="button" onClick={() => {
        updateDeliverable(deliverable.id, {
          visibleToClient: !deliverable.visibleToClient
        });
        edit({
          visibleToClient: !deliverable.visibleToClient
        });
      }} aria-pressed={deliverable.visibleToClient} className={['label shrink-0 border px-3 py-2 transition-colors duration-150 ease-out', deliverable.visibleToClient ? 'border-paper bg-paper text-ink' : 'border-paper/30 text-paper/40 hover:border-paper/60'].join(' ')} data-id="element-191" data-mp="a09vdbbfytn7">
          {deliverable.visibleToClient ? 'Visible' : 'Hidden'}
        </button>

        <div className="flex shrink-0 items-center gap-6" data-id="element-192" data-mp="19tygv13brb8z">
          <button type="button" onClick={() => {
          setDraft(deliverable);
          setPanel(current => current === 'edit' ? 'closed' : 'edit');
          setConfirmRemove(false);
        }} className={BUTTON_QUIET} data-id="element-193" data-mp="19rc9p2kcixra">
            {editingRow ? 'Close' : 'Edit'}
          </button>
          <button type="button" onClick={() => setConfirmRemove(true)} className="label text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-194" data-mp="1tg4lqyv3o27g">
            Remove
          </button>
        </div>
      </div>

      {confirmRemove && <div className="mb-6 border border-paper/30 px-6 py-6" data-id="element-195" data-mp="27fw75kny4kvh">
          <p className="font-display text-xl leading-snug" data-id="element-196" data-mp="8434p8ie2nbd">
            Remove {deliverable.name} from the dashboard?
          </p>
          <p className="label-sm mt-3 text-paper/40" data-id="element-197" data-mp="d3z9vb2toksi">
            This only removes it from the client dashboard. Nothing is deleted from Google Drive.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-6" data-id="element-198" data-mp="syj9xx1vspz">
            <button type="button" onClick={() => {
          removeDeliverable(deliverable.id);
          notify(`${deliverable.name} removed from dashboard`);
        }} className={BUTTON_SOLID} data-id="element-199" data-mp="rdktx7m7pmu">
              Remove
            </button>
            <button type="button" onClick={() => setConfirmRemove(false)} className={BUTTON_QUIET} data-id="element-200" data-mp="ofwwmmpvikjo">
              Cancel
            </button>
          </div>
        </div>}

      {open && <div className="mb-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-201" data-mp="em685htqya3z">
          {editingRow && <div className="lg:col-span-7" data-id="element-202" data-mp="9ncyy1lht59m">
            <label className="block" data-id="element-203" data-mp="2zbdikaxmwbdo">
              <span className="label block text-paper/40" data-id="element-204" data-mp="2z87soth6l5gw">Name</span>
              <input value={draft.name} onChange={event => edit({
            name: event.target.value
          })} placeholder="e.g. Event Highlights Film" className={FIELD} data-id="element-205" data-mp="2uag3i77x7xge" />
            </label>

            <div className="mt-8 grid grid-cols-2 gap-8 sm:grid-cols-3" data-id="element-206" data-mp="3ayyfoddrrxyh">
              <label className="block" data-id="element-207" data-mp="3b0rq83ri72br">
                <span className="label block text-paper/40" data-id="element-208" data-mp="3ezqq39ydo8wc">Type</span>
                <select value={draft.type} onChange={event => edit({
              type: event.target.value as Deliverable['type']
            })} className={SELECT} data-id="element-209" data-mp="3f23eo5yn5hid">
                  {DELIVERABLE_TYPES.map(option => <option key={option} value={option} className={OPTION} data-id="element-210" data-mp="3txaqs4o8rhwp">
                      {option}
                    </option>)}
                </select>
              </label>
              <label className="block" data-id="element-211" data-mp="pi1jhrrrd0i5">
                <span className="label block text-paper/40" data-id="element-212" data-mp="u9t0pbp0xtvm">Quantity</span>
                <input value={draft.quantity} onChange={event => edit({
              quantity: event.target.value
            })} placeholder="e.g. 8" className={FIELD} data-id="element-213" data-mp="ubvzak48svby" />
              </label>
              {isVersioned(draft.status) ? <label className="block" data-id="element-214" data-mp="2os43krcaw221">
                  <span className="label block text-paper/40" data-id="element-215" data-mp="2js7zks3lsamh">Version</span>
                  <input value={draft.version} onChange={event => edit({
              version: event.target.value
            })} placeholder="e.g. V3" className={FIELD} data-id="element-216" data-mp="1w1cesexkefoq" />
                </label> : <div className="block" data-id="element-217" data-mp="210p0uxgm3iq9">
                  <span className="label block text-paper/40" data-id="element-218" data-mp="20zvys9sllheg">Version</span>
                  <p className="mt-3 border-b border-paper/25 pb-2 font-sans text-sm tracking-wide2 text-paper/30" data-id="element-219" data-mp="3hefccpa2999x">
                    Added once In Review
                  </p>
                </div>}
            </div>

            <label className="mt-8 block" data-id="element-220" data-mp="wcsp4qmn97dt">
              <span className="label block text-paper/40" data-id="element-221" data-mp="re8ywe10p9fn">Status</span>
              <select value={draft.status} onChange={event => edit({
            status: event.target.value as Deliverable['status']
          })} className={SELECT} data-id="element-222" data-mp="rf0u3nqpd55e">
                {DELIVERABLE_STATUSES.map(option => <option key={option} value={option} className={OPTION} data-id="element-223" data-mp="cqea38q4rp7u">
                    {option}
                  </option>)}
              </select>
              {/* Stage follows the status, so there is nothing else to set. */}
              <span className="label-sm mt-3 block text-paper/30" data-id="element-224" data-mp="3uuxxnduczeur">
                Stage · {stageHeading(deliverableStage(draft.status))} · set from status
              </span>
            </label>

            {/* Only video needs framing — it decides the client player's shape. */}
            {draft.type === 'Video' && <label className="mt-8 block" data-id="element-225" data-mp="20nnfi1ud2tev">
                <span className="label block text-paper/40" data-id="element-226" data-mp="20mu58qw71lrz">Video Format</span>
                <select value={draft.videoFormat ?? '16:9 Landscape'} onChange={event => edit({
            videoFormat: event.target.value as Deliverable['videoFormat']
          })} className={SELECT} data-id="element-227" data-mp="25kvww07oqtf2">
                  {VIDEO_FORMATS.map(option => <option key={option} value={option} className={OPTION} data-id="element-228" data-mp="1mncmot9hr4r2">
                      {option}
                    </option>)}
                </select>
              </label>}

            {draft.type === 'Transcript' && <TranscriptUpload value={draft.transcript ?? ''} onChange={text => edit({
          transcript: text
        })} data-id="element-229" data-mp="1wn3gstu53hqm" />}

            <label className="mt-8 block" data-id="element-230" data-mp="37zmf272ofgw8">
              <span className="label block text-paper/40" data-id="element-231" data-mp="382s4jnf7wksh">Google Drive Link</span>
              <input value={draft.driveUrl} onChange={event => edit({
            driveUrl: event.target.value
          })} placeholder="Paste Drive file or folder link" className={FIELD} data-id="element-232" data-mp="17wv273ykq1we" />
              {draft.driveUrl.trim() && <span className="label-sm mt-3 block text-paper/30" data-id="element-233" data-mp="1cy0ux1s8hp5x">
                  {toDrivePreviewUrl(draft.driveUrl) ? `Embeds as ${toDrivePreviewUrl(draft.driveUrl)}` : isDriveFolder(draft.driveUrl) ? 'Folder link · opens in Drive, cannot play inside Portal' : 'Link not recognised as a Drive file — the client will see a preview-unavailable message'}
                </span>}
            </label>

            <label className="mt-8 block" data-id="element-234" data-mp="5iebkp49ztn1">
              <span className="label block text-paper/40" data-id="element-235" data-mp="1ic9wfyjj07r">Drive Audio Link (optional)</span>
              <input value={draft.audioUrl} onChange={event => edit({
            audioUrl: event.target.value
          })} placeholder="Paste Drive audio link" className={FIELD} data-id="element-236" data-mp="3njtb1zxaae7s" />
            </label>

            <label className="mt-8 block" data-id="element-237" data-mp="3dn6rrfklvv7v">
              <span className="label block text-paper/40" data-id="element-238" data-mp="3ikq8cm4s0k2z">Internal Note</span>
              <input value={draft.internalNote} onChange={event => edit({
            internalNote: event.target.value
          })} placeholder="Portal only — never shown to the client" className={FIELD} data-id="element-239" data-mp="3iim5m7im5r4a" />
            </label>

            <label className="mt-8 flex items-center justify-between gap-6 border-b border-paper/25 pb-3" data-id="element-240" data-mp="6h8wrlkn5ni">
              <span className="label text-paper/40" data-id="element-241" data-mp="57wv4mzhji7h">Visible to Client</span>
              <button type="button" onClick={() => edit({
            visibleToClient: !draft.visibleToClient
          })} aria-pressed={draft.visibleToClient} className={['label border px-3 py-2 transition-colors duration-150 ease-out', draft.visibleToClient ? 'border-paper bg-paper text-ink' : 'border-paper/30 text-paper/40 hover:border-paper/60'].join(' ')} data-id="element-242" data-mp="2kw6ioq7f419c">
                {draft.visibleToClient ? 'On' : 'Off'}
              </button>
            </label>

            <div className="mt-10 flex flex-wrap items-center gap-5" data-id="element-243" data-mp="3p1a38yxfogap">
              <button type="button" onClick={save} className={BUTTON_SOLID} data-id="element-244" data-mp="3k407l1jgb8ef">
                Save Changes
              </button>
              <button type="button" onClick={publish} className={BUTTON_GHOST} data-id="element-245" data-mp="3f2147rnpobhh">
                Publish New Version
              </button>
            </div>
            <p className="label-sm mt-6 max-w-md leading-relaxed text-paper/30" data-id="element-246" data-mp="23oyvtjvrms8e">
              Save Changes updates the current client-facing version without creating a new review
              round. Publish New Version creates a new review version and marks it ready for client
              review.
            </p>
          </div>}

          <div className={editingRow ? 'lg:col-span-5 lg:border-l lg:border-ruleDark lg:pl-12' : 'lg:col-span-7'} data-id="element-247" data-mp="j4lfmnapa8sh">
            {/* Outstanding feedback leads, so nothing needing action is buried. */}
            {outstanding.length === 0 && (deliverable.type === 'Transcript' ? <TranscriptRevisions deliverable={deliverable} data-id="element-248" data-mp="11vkyr7vikodu" /> : <VersionHistory deliverable={deliverable} data-id="element-249" data-mp="16xk24hr97las" />)}

            <h4 className={`label border-b border-ruleDark pb-4 text-paper/40 ${outstanding.length > 0 ? '' : 'mt-12'}`} data-id="element-250" data-mp="1bvvb6q9q5rry">
              Requested Changes
            </h4>
            <ul className="m-0 list-none p-0" data-id="element-251" data-mp="3dnhxwfml30im">
              {outstanding.length === 0 && <li className="py-5 font-display text-lg text-paper/40" data-id="element-252" data-mp="35msdq629m8mq">Nothing outstanding.</li>}
              {outstanding.map(request => <li key={request.id} className="border-b border-ruleDark py-5" data-id="element-253" data-mp="3fjx30ereqk7r">
                  <span className="label block text-paper" data-id="element-254" data-mp="3fhu3n44d7eqd">
                    {request.timecode ? `${request.timecode} · ` : ''}
                    {request.authorName}
                  </span>
                  <p className="mt-2 font-display text-lg leading-snug" data-id="element-255" data-mp="3oiihnytrdf39">{request.body}</p>
                  <div className="mt-3 flex items-center justify-between gap-4" data-id="element-256" data-mp="3kh6yab1h9q3i">
                    <span className="label-sm text-paper/35" data-id="element-257" data-mp="24s8uzs0ajdc">
                      {request.version ? `${request.version} · ` : ''}
                      Sent {request.time || request.submitted}
                    </span>
                    <button type="button" onClick={() => {
                resolveChangeRequest(deliverable.id, request.id);
                notify('Marked as completed');
              }} className="label-sm shrink-0 text-paper/60 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-paper" data-id="element-258" data-mp="c13q846gdzen">
                      Mark completed
                    </button>
                  </div>
                </li>)}
            </ul>

            {completed.length > 0 && <>
                <h4 className="label mt-12 border-b border-ruleDark pb-4 text-paper/40" data-id="element-259" data-mp="btfssqo1jfd0">
                  Completed Changes
                </h4>
                <ul className="m-0 list-none p-0" data-id="element-260" data-mp="3u8qdfihz6wnx">
                  {completed.map(request => <li key={request.id} className="border-b border-ruleDark py-5" data-id="element-261" data-mp="30u9n9ghz6nn">
                      <span className="label block text-paper/60" data-id="element-262" data-mp="3k998x4egmotj">
                        {request.timecode ? `${request.timecode} · ` : ''}
                        {request.authorName}
                      </span>
                      <p className="mt-2 font-display text-lg leading-snug text-paper/60" data-id="element-263" data-mp="8jmtejj3041e">
                        {request.body}
                      </p>
                      <span className="label-sm mt-3 block text-paper/35" data-id="element-264" data-mp="3vochkubk4qij">
                        {request.version ? `${request.version} · ` : ''}
                        Requested {request.time || request.submitted}
                        {request.completedAt ? ` · Completed ${request.completedAt}` : ' · Completed'}
                        {request.completedBy ? ` by ${request.completedBy}` : ''}
                      </span>
                    </li>)}
                </ul>
              </>}

            {outstanding.length > 0 && <div className="mt-12" data-id="element-265" data-mp="330gmtv5jt8oe">
                {deliverable.type === 'Transcript' ? <TranscriptRevisions deliverable={deliverable} data-id="element-266" data-mp="2y1dhe9lnp0fw" /> : <VersionHistory deliverable={deliverable} data-id="element-267" data-mp="2y3py6jfoh4rd" />}
              </div>}

          </div>
        </div>}
    </Reorder.Item>;
}